import React, { useEffect, useRef } from 'react';
import { Box, Heading, Text, TextButton } from '@wix/design-system';
import { useConfigContext } from '../../config-context';
import { useMarketplaceStore } from '../../component-decorators/with-marketplace-store';
import { SolutionGridWrapper } from '../solution-grid-wrapper/solution-grid-wrapper';
import { ArrowLeft } from '@wix/wix-ui-icons-common';
import { config } from '../../config';
import { Origin, Path, ErrorPageType } from '../../enums/marketplace-enums';
import { WithFetcher } from '../../component-decorators/with-fetcher';
import { CenteredLoader } from '../centered-loader';
import type {
  GetWebSolutionsByTagsResponse,
  WebSolutionBase,
} from '@wix/ambassador-marketplace/types';
import { Order } from '@wix/ambassador-marketplace/types';
import { httpClient } from '../../http-client';
import type { AppMarketRpcClient } from '../../types/app-market-rpc-client';
import { ErrorPageContainer } from '../error-page/error-page-container';
import { composer } from '../../component-decorators/composer';
import type { TFunction } from 'i18next';
import { biLogger } from '../../bi';
import { appMarketPageView } from '@wix/bi-logger-market/v2';
import { getPathForBi, getWidgetReferral } from '../../common/bi-events';

type SubCategory = {
  slug: string;
  name: string;
  apps: (WebSolutionBase & { appIndex: string; isPreInstalled: boolean })[];
  parentCategory: { slug: string; name: string };
};

async function getSubCategoryData(
  slug: string,
  languageCode: string,
  countryCode: string = 'US',
  rpcClient?: AppMarketRpcClient,
): Promise<SubCategory> {
  const shouldUseRpcClient =
    typeof window === 'undefined' && rpcClient !== undefined;

  const getWebSolutionsByTagsResponse = shouldUseRpcClient
    ? await rpcClient.getWebSolutionsByTags({
        country: countryCode,
        lang: languageCode,
        limit: 200,
        order: Order.GEO,
        tagsSlug: slug,
      })
    : await httpClient
        .get<GetWebSolutionsByTagsResponse>(
          `${config.baseApiUrl}/_api/marketplace-api/v1/web-solutions/tags/${slug}?limit=200&lang=${languageCode}&order=GEO&country=${countryCode}`,
        )
        .then(({ data }) => data);

  const name =
    getWebSolutionsByTagsResponse.tagsAndSubCategories?.[0]?.tag?.name;

  const apps = getWebSolutionsByTagsResponse.webSolutions?.map((app) => ({
    appIndex: app.baseInfo?.id ?? '',
    isPreInstalled: app?.baseInfo?.showRegularAppPage ?? false,
    ...app.baseInfo,
  }));

  const parentCategorySlug =
    getWebSolutionsByTagsResponse.tagsAndSubCategories?.[0]?.tag?.data
      ?.categoryData?.parentSlug;

  if (!parentCategorySlug) {
    return {
      slug,
      name,
      apps,
      parentCategory: { slug: undefined, name: undefined },
    };
  }

  const getParentCategoryWebSolutionsByTagsResponse = shouldUseRpcClient
    ? await rpcClient.getWebSolutionsByTags({
        country: countryCode,
        lang: languageCode,
        limit: 200,
        order: Order.GEO,
        tagsSlug: parentCategorySlug,
      })
    : await httpClient
        .get<GetWebSolutionsByTagsResponse>(
          `${config.baseApiUrl}/_api/marketplace-api/v1/web-solutions/tags/${parentCategorySlug}?limit=200&lang=${languageCode}&order=GEO&country=${countryCode}`,
        )
        .then(({ data }) => data);

  const parentCategoryName =
    getParentCategoryWebSolutionsByTagsResponse.tagsAndSubCategories?.[0]?.tag
      ?.name;

  const parentCategory = {
    slug: parentCategorySlug,
    name: parentCategoryName,
  };

  return { slug, name, apps, parentCategory };
}

export function SubCategoryPage() {
  const isInStandaloneMarket = config.origin === Origin.STANDALONE;
  const { route, prevRoute, metaSiteId } = useMarketplaceStore();
  const { locale: languageCode, countryCode, rpcClient } = useConfigContext();
  const isFirstRenderRef = useRef<boolean>(true);
  const refetchRef = useRef<any>(() => {});

  useEffect(() => {
    if (!isFirstRenderRef.current) {
      refetchRef.current(() =>
        getSubCategoryData(route.subCategories[0], languageCode, countryCode),
      );
    }

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    }
  }, [route.subCategories[0], languageCode]);

  useEffect(() => {
    biLogger.report(
      appMarketPageView({
        app_id: null,
        market: config.originForBI,
        navigation_source: route.referral,
        tag_name: route.slug.replace(/-/g, '_'),
        sub_tag_name: route.subCategories?.[0]?.replace(/-/g, '_'),
        tag_type: 'category',
        msid: metaSiteId,
        referral_info: getPathForBi(prevRoute?.path),
        search_term: prevRoute.query,
        widget_referral: getWidgetReferral(),
        utm_id: route.utm_id,
        referral_tag_name: prevRoute?.slug?.replace(/-/g, '_'),
        referral_sub_tag_name: prevRoute?.subCategories?.join(','),
        is_external: route?.referral && route?.referral !== 'manage_apps',
      }),
    );
  }, [route.slug, route.subCategories?.[0]]);

  return (
    <WithFetcher
      initialFetch={() =>
        getSubCategoryData(
          route.subCategories[0],
          languageCode,
          countryCode,
          rpcClient,
        )
      }
      fetchMemoizationId={`sub-category-${route.subCategories[0]}-${languageCode}`}
      loader={<CenteredLoader />}
    >
      {({ fetchData: subCategory, reFetch }) => {
        refetchRef.current = reFetch;

        if (!isValidSubCategory(subCategory)) {
          return (
            <ErrorPageContainer
              slug={route.subCategories[0]}
              errorPageType={ErrorPageType.NOT_FOUND}
            />
          );
        }

        return (
          <Box
            align="center"
            paddingTop="24px"
            paddingBottom="24px"
            paddingLeft="48px"
            paddingRight="48px"
            direction="vertical"
          >
            <Box direction="vertical" minWidth="864px" maxWidth="1248px">
              <Box paddingBottom="24px" direction="vertical" gap="6px">
                <TextButton
                  as="a"
                  href={
                    isInStandaloneMarket
                      ? `/app-market/${Path.CATEGORY}/${subCategory.parentCategory.slug}`
                      : undefined
                  }
                  size="medium"
                  weight="normal"
                  prefixIcon={<ArrowLeft />}
                  onClick={(event) => {
                    event.preventDefault();
                    config.goto({
                      path: Path.CATEGORY,
                      slug: subCategory.parentCategory.slug,
                      referralTag: subCategory.slug,
                    });
                  }}
                >
                  {subCategory.parentCategory.name}
                </TextButton>

                <Heading as="h1">{subCategory.name}</Heading>
              </Box>

              <Text weight="thin">
                <AppsCount count={subCategory.apps.length} />
              </Text>

              <Box marginTop="-3px">
                <SolutionGridWrapper solutions={subCategory.apps} />
              </Box>
            </Box>
          </Box>
        );
      }}
    </WithFetcher>
  );
}

// ------------------------------HELPER-FUNCTIONS------------------------------

function isValidSubCategory(
  maybeSubCategory: unknown,
): maybeSubCategory is SubCategory {
  return (
    typeof (maybeSubCategory as SubCategory).slug === 'string' &&
    typeof (maybeSubCategory as SubCategory).name === 'string' &&
    typeof (maybeSubCategory as SubCategory).parentCategory?.slug ===
      'string' &&
    typeof (maybeSubCategory as SubCategory).parentCategory?.name ===
      'string' &&
    Array.isArray((maybeSubCategory as SubCategory).apps)
  );
}

const AppsCount = composer()
  .withTranslation()
  .compose(({ count, t }: { count: number; t?: TFunction }) =>
    t('sub-category-page.apps-count', { count }),
  );
